import { FileText, Save } from "lucide-react";

import type { CelFactura } from "@/lib/api";

const num = (n: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: d }).format(n);

/**
 * Resumen de la factura de CERTIFICADOS — CEL o i-REC (vista CFDI: nº de
 * certificados × precio unitario, unidad SAT MAW). Una Orden de Compra = una
 * factura, sin parcialidades. Compartido entre /facturacion (con botón
 * Guardar) y el detalle de /facturas (solo lectura, sin `onGuardar`).
 */
export function ResumenCel({ r, onGuardar, guardando }: {
  r: CelFactura;
  onGuardar?: () => void;
  guardando?: boolean;
}) {
  const money = (n: number, d = 2) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: r.moneda === "USD" ? "USD" : "MXN",
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(n);
  const pct = `${(r.iva_pct * 100).toFixed(0)}%`;
  const producto = r.producto ?? "CEL";
  const manuales = r.manuales ?? [];
  const entregas = r.entregas ?? [];

  return (
    <div className="space-y-4">
      {r.advertencias.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm space-y-1">
          {r.advertencias.map((a, i) => (
            <div key={i}>⚠ {a}</div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-slate-800">
            {r.folio} — {r.contraparte}
          </span>
          <span className="text-xs text-slate-500">
            Periodo {r.ym} ·{" "}
            <b>{new Intl.NumberFormat("es-MX").format(r.cel)} {producto}s</b> ·{" "}
            <b>{r.moneda}</b>
            {r.vigencia_desde && ` · OC ${r.vigencia_desde} a ${r.vigencia_hasta ?? "—"}`}
          </span>
        </div>

        {/* OC por entregas: cuál se factura y cuáles ya están guardadas */}
        {entregas.length > 0 && (
          <div className="px-4 py-2 border-b text-xs text-slate-600 flex flex-wrap items-center gap-2">
            <span className="text-slate-500">Entregas de la OC:</span>
            {entregas.map((e) => (
              <span
                key={e.n}
                className={`rounded-full px-2 py-0.5 border ${
                  e.n === r.n_pago
                    ? "bg-teal-100 border-teal-300 text-teal-800 font-semibold"
                    : "bg-white border-slate-200"
                }`}
                title={e.factura_id ? `Ya facturada (#${e.factura_id})` : "Sin factura guardada"}
              >
                {e.n}. {e.ym} · {new Intl.NumberFormat("es-MX").format(e.cel)} {producto} ·{" "}
                {money(e.importe)}
                {e.factura_id ? " ✓" : ""}
              </span>
            ))}
          </div>
        )}

        {/* Concepto, igual que en el CFDI */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="px-3 py-2 text-right">Cantidad</th>
                <th className="px-3 py-2">Unidad SAT</th>
                <th className="px-3 py-2">Clave Producto-Servicio</th>
                <th className="px-3 py-2">Concepto / Descripción</th>
                <th className="px-3 py-2 text-right">Valor unitario</th>
                <th className="px-3 py-2 text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="px-3 py-2 text-right tabular-nums">{num(r.cel, 2)}</td>
                <td className="px-3 py-2">{r.clave_unidad} — Megawatt</td>
                <td className="px-3 py-2">{r.clave_prod_serv} — Servicios eléctricos</td>
                <td className="px-3 py-2">{r.descripcion}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.precio_unitario)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{money(r.importe)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Totales (con los cargos/créditos manuales guardados, si los hay) */}
        <div className="border-t px-4 py-3 flex flex-col items-end gap-1 text-sm">
          {manuales.length > 0 && (
            <>
              <div className="flex gap-8">
                <span className="text-slate-500">Importe de los {producto}</span>
                <span className="tabular-nums w-40 text-right">{money(r.importe)}</span>
              </div>
              {manuales.map((l, i) => (
                <div key={i} className="flex gap-8">
                  <span className="text-slate-500">
                    {l.monto >= 0 ? "Cargo manual" : "Crédito manual"}: {l.concepto}
                  </span>
                  <span className={`tabular-nums w-40 text-right ${l.monto < 0 ? "text-emerald-700" : ""}`}>
                    {money(l.monto)}
                  </span>
                </div>
              ))}
            </>
          )}
          <div className="flex gap-8">
            <span className="text-slate-500">SUBTOTAL</span>
            <span className="tabular-nums w-40 text-right">
              {money(r.importe + manuales.reduce((s, l) => s + l.monto, 0))}
            </span>
          </div>
          <div className="flex gap-8">
            <span className="text-slate-500">I.V.A. ({pct})</span>
            <span className="tabular-nums w-40 text-right">{money(r.iva)}</span>
          </div>
          <div className="flex gap-8 font-semibold text-emerald-800 border-t pt-1">
            <span>TOTAL</span>
            <span className="tabular-nums w-40 text-right">{money(r.total)}</span>
          </div>
        </div>

        <div className="px-4 py-2 border-t text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
          <span>
            Cálculo: {new Intl.NumberFormat("es-MX").format(r.cel)} {producto}s ×{" "}
            {money(r.precio_unitario)} por certificado
          </span>
          {r.tc_informativo != null && (
            <span>
              Tipo de cambio informativo: {num(r.tc_informativo, r.moneda === "USD" ? 4 : 2)}
              {r.tc_fecha ? ` (${r.tc_fecha})` : ""}
            </span>
          )}
          <span>
            {entregas.length > 0
              ? `Una factura por entrega (entrega ${r.n_pago} de ${entregas.length}).`
              : `Una factura por Orden de Compra (los ${producto} no llevan parcialidades).`}
          </span>
        </div>
      </div>

      {onGuardar && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onGuardar}
            disabled={guardando}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {guardando ? "Guardando…" : "Guardar factura"}
          </button>
          <span className="text-xs text-slate-400">
            Guarda la factura {entregas.length > 0 ? "de esta entrega" : "de la OC (una por Orden de Compra)"} y
            abre su detalle, donde están el PDF y Enviar a Flamma.
          </span>
        </div>
      )}
    </div>
  );
}
